import { useAuthStore } from '@/state/store/auth';

/**
 * The Data Gateway's `get<Schema>s` queries moved from a single `input: DynamicQueryInput`
 * argument (JSON-string `filter`/`sort` + flat `pageNo`/`pageSize`) to native `where` / `order` /
 * `paging` arguments. The generated `where`/`order` input type names aren't reliably guessable
 * (see the blocks-data-gateway-crud skill's pluralization gotcha for the same problem on query
 * field names), so rather than declaring typed GraphQL variables for them, callers build the
 * argument values as plain JS objects and this module inlines them into the query string as
 * GraphQL literals (unquoted object keys, quoted strings).
 *
 * `where` is a HotChocolate-style filter input (a standard .NET GraphQL filtering convention):
 * every field's clause must be an operation-filter object (`StringOperationFilterInput`,
 * `ComparableOperationFilterInput`, `BooleanOperationFilterInput`, ...), never a bare scalar —
 * confirmed by a live server error when a bare string was sent for equality ("The syntax node
 * `StringValue` is incompatible with the type `StringOperationFilterInput`"). So a bare
 * `{ Field: "value" }` filter must become `{ Field: { eq: "value" } }`, and this codebase's
 * existing Mongo-style range/pattern operators (`$gte`, `$lte`, `$in`, ...) just lose their `$`
 * sigil (`{ Field: { $gte: x } }` -> `{ Field: { gte: x } }`) — the latter confirmed working via a
 * manual test query (`getMentorshipAssignments(where: { LastUpdatedDate: { gte: "..." } }, ...)`).
 */
const toGraphQLLiteral = (value: unknown): string => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `[${value.map(toGraphQLLiteral).join(', ')}]`;
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== undefined
    );
    return `{ ${entries.map(([key, v]) => `${key}: ${toGraphQLLiteral(v)}`).join(', ')} }`;
  }
  return 'null';
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A field's filter value already looks like an operator clause (built by this codebase's
 * existing Mongo-style filter helpers, e.g. `containsFilter`/date-range builders) if any of its
 * keys carry the `$` sigil. */
const isOperatorClause = (value: Record<string, unknown>): boolean =>
  Object.keys(value).some((key) => key.startsWith('$'));

/**
 * Most Mongo-style operator keys map onto the HotChocolate operation-filter field of the same
 * name once the `$` sigil is dropped (`$gte` -> `gte`), confirmed for `gte` via a manual test
 * query. `$ne` is the one confirmed exception — HotChocolate names it `neq`, so a blind sigil
 * strip would silently target a field that doesn't exist. Anything not listed here just has its
 * `$` stripped (see the `??` fallback in `shapeFieldClause`).
 */
const OPERATOR_KEY_MAP: Record<string, string> = { $ne: 'neq' };

const stripOperatorSigil = (key: string): string => (key.startsWith('$') ? key.slice(1) : key);

/**
 * Shapes one field's filter value into a HotChocolate operation-filter clause:
 * - a bare scalar becomes `{ eq: value }`;
 * - `{ $regex: text, $options: 'i' }` (this codebase's case-insensitive "contains" search
 *   pattern, e.g. `containsFilter` helpers) becomes `{ contains: text }` — `StringOperationFilterInput`
 *   has no `regex`/`options` field, so `contains` is the closest supported operator; its exact
 *   case-sensitivity versus `$options: 'i'` is UNCONFIRMED and worth testing wherever search boxes
 *   use it;
 * - any other existing Mongo-style operator object has its `$` sigils mapped/stripped.
 */
const shapeFieldClause = (value: unknown): Record<string, unknown> => {
  if (!isPlainObject(value) || !isOperatorClause(value)) return { eq: value };
  if ('$regex' in value) return { contains: value.$regex };
  return Object.fromEntries(
    Object.entries(value).map(([key, v]) => [OPERATOR_KEY_MAP[key] ?? stripOperatorSigil(key), v])
  );
};

/** Shapes a whole filter object (as built by this codebase's existing Mongo-style filter helpers)
 * into the Data Gateway's `where` input shape, recursing into `$and`/`$or` clause arrays. */
const shapeWhere = (filter: Record<string, unknown>): Record<string, unknown> => {
  const shaped: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined) continue;
    if (key === '$and' || key === '$or') {
      shaped[key === '$and' ? 'and' : 'or'] = Array.isArray(value)
        ? value.map((clause) => shapeWhere(clause as Record<string, unknown>))
        : [];
      continue;
    }
    shaped[key] = shapeFieldClause(value);
  }
  return shaped;
};

/**
 * Every query is scoped to the active organization: `OrganizationId` is a platform-managed field on
 * every entity, and the account's `me` record says which organization the session is in (the literal
 * `"default"` for the built-in default organization, which is scoped like any other). A caller that
 * already filters on `OrganizationId` keeps its own value.
 */
const withActiveOrganization = (where: Record<string, unknown>): Record<string, unknown> => {
  const organizationId = useAuthStore.getState().selectedOrgId;
  if (!organizationId || 'OrganizationId' in where) return where;
  return { ...where, OrganizationId: organizationId };
};

/** Builds the inline `where: {...}` argument text for a query's dynamic filter object. */
export const buildWhereLiteral = (where: Record<string, unknown> = {}): string =>
  toGraphQLLiteral(shapeWhere(withActiveOrganization(where)));

export type SortDirection = 'ASC' | 'DESC';

/** One `order` entry; a list's order is an array of these, first field first. */
export interface OrderBy {
  field: string;
  direction: SortDirection;
}

/**
 * Builds the inline `order: [...]` argument text: `[{ field: "AttendanceDate", direction: DESC }]`
 * (verified against the Data Gateway). Sort on the server whenever a list is paged: sorting only the
 * returned page shows an arbitrary slice. `[]` keeps the gateway's own order. String fields sort
 * ordinally (case-sensitive: "IT" before "Insurance", lowercase after uppercase).
 */
export const buildOrderLiteral = (order: OrderBy[] = []): string =>
  `[${order
    .filter(({ field }) => /^[A-Za-z_][A-Za-z0-9_]*$/.test(field))
    .map(
      ({ field, direction }) =>
        `{ field: "${field}", direction: ${direction === 'ASC' ? 'ASC' : 'DESC'} }`
    )
    .join(', ')}]`;

/** Builds the inline `paging: {...}` argument text. */
export const buildPagingLiteral = (pageNo: number, pageSize: number): string =>
  toGraphQLLiteral({ pageNo, pageSize });
